import { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { X, Copy, Check, Loader2 } from 'lucide-react'
import { apiService } from '@/services/api'

interface DonateModalProps {
  reserveSlug: string
  reserveName: string
  onClose: () => void
}

export default function DonateModal({ reserveSlug, reserveName, onClose }: DonateModalProps) {
  const [address, setAddress] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const generateAddress = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiService.getNextAddress(reserveSlug)
      setAddress(response.data.address)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate address')
    } finally {
      setLoading(false)
    }
  }

  const copyAddress = async () => {
    if (!address) return
    await navigator.clipboard.writeText(address)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <h2 className="text-xl font-bold text-white mb-2">Donate Bitcoin</h2>
        <p className="text-gray-400 mb-6">Support {reserveName}</p>

        {!address && !loading && !error && (
          <button
            onClick={generateAddress}
            className="w-full bg-bitcoin hover:bg-bitcoin-dark text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Generate Receive Address
          </button>
        )}

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-bitcoin" size={32} />
            <span className="ml-3 text-gray-300">Generating address...</span>
          </div>
        )}

        {error && (
          <div className="text-center">
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={generateAddress}
              className="text-bitcoin hover:text-bitcoin-light underline"
            >
              Try again
            </button>
          </div>
        )}

        {address && (
          <div className="text-center space-y-4">
            <div className="bg-white p-4 rounded-lg inline-block">
              <QRCodeSVG value={`bitcoin:${address}`} size={200} />
            </div>

            <div className="bg-gray-900 rounded-lg p-3">
              <p className="font-mono text-sm text-gray-300 break-all">{address}</p>
            </div>

            <button
              onClick={copyAddress}
              className="flex items-center justify-center gap-2 w-full bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors"
            >
              {copied ? (
                <>
                  <Check size={16} />
                  Copied!
                </>
              ) : (
                <>
                  <Copy size={16} />
                  Copy Address
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
